import { useEffect, useState, useMemo, useRef, useCallback, useId } from 'react';
import { useWorkspaceNavigate as useNavigate } from '@/contexts/DocumentWorkspace';
import { useTranslation } from 'react-i18next';
import {
  Search, ArrowRight, Clock, Wrench, UserCircle, FileText, CornerDownLeft,
  CreditCard, Cloud, Send, Settings, HelpCircle, Mail, X,
} from 'lucide-react';
import { CATEGORIES, localizedToolTitle, localizedLabel } from '../lib/toolCategories';
import { getRecent } from '../lib/recentFiles';
import { isDesktop } from '../lib/fileSystem';

// Items that aren't part of the tool registry — billing, account, eSign,
// support pages. Stored here (not in toolCategories) because they're "pages
// you can jump to," not "tools you run on a file."
const PAGE_ITEMS = [
  { id: 'account',         path: '/account',                  icon: UserCircle, titleKey: 'common:nav.my_account',         fallback: 'My Account' },
  { id: 'billing',         path: '/account/billing',          icon: CreditCard, titleKey: 'common:nav.billing',            fallback: 'Billing & plan' },
  { id: 'cloud',           path: '/cloud',                    icon: Cloud,      titleKey: 'common:nav.cloud_documents',    fallback: 'Cloud documents' },
  { id: 'esign',           path: '/esign',                    icon: Send,       titleKey: 'esign:nav.dashboard',           fallback: 'eSign Dashboard' },
  { id: 'esign-templates', path: '/esign/templates',          icon: FileText,   titleKey: 'esign:nav.templates',           fallback: 'eSign Templates' },
  { id: 'esign-settings',  path: '/account/esign-settings',   icon: Settings,   titleKey: 'esign:nav.settings',            fallback: 'eSign Settings' },
  { id: 'help',            path: '/help',                     icon: HelpCircle, titleKey: 'marketing:nav.help',            fallback: 'Help' },
  { id: 'contact',         path: '/contact',                  icon: Mail,       titleKey: 'marketing:nav.contact',         fallback: 'Contact' },
];

export default function CommandPalette() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [recents, setRecents] = useState([]);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const previouslyFocusedRef = useRef(null);
  const listboxId = useId();

  // Build the master item list (Tools + Pages + Recents), then filter by query.
  const allItems = useMemo(() => {
    const tools = CATEGORIES.flatMap((cat) =>
      cat.tools.map((tool) => ({
        kind: 'tool',
        key: `tool:${tool.path}`,
        path: tool.path,
        icon: tool.icon,
        title: localizedToolTitle(t, tool),
        subtitle: localizedLabel(t, cat),
        haystack: [
          localizedToolTitle(t, tool),
          tool.title || '',
          localizedLabel(t, cat),
          tool.path || '',
        ].join(' ').toLowerCase(),
      })),
    );
    const pages = PAGE_ITEMS.map((p) => ({
      kind: 'page',
      key: `page:${p.id}`,
      path: p.path,
      icon: p.icon,
      title: tResolved(t, p.titleKey, p.fallback),
      subtitle: t('common:nav.pages', 'Pages'),
      haystack: [tResolved(t, p.titleKey, p.fallback), p.fallback, p.path].join(' ').toLowerCase(),
    }));
    const recentItems = recents.map((r) => ({
      kind: 'recent',
      key: `recent:${r.path || r.name}:${r.openedAt || ''}`,
      // Desktop has a path; web only has a name. Either way: open the viewer.
      file: r,
      icon: FileText,
      title: r.name,
      subtitle: t('common:command_palette.recent_subtitle', 'Recent file'),
      haystack: (r.name || '').toLowerCase(),
    }));
    return [...recentItems, ...tools, ...pages];
  }, [t, recents]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allItems;
    return allItems
      .map((item) => ({ item, score: scoreItem(item, q) }))
      .filter((s) => s.score > 0)
      .sort((a, b) => b.score - a.score)
      .map((s) => s.item);
  }, [allItems, query]);

  // Group results by kind for section headers, preserving the sorted order.
  const grouped = useMemo(() => groupBySection(results), [results]);

  // Clamp the selected index whenever the result set shrinks.
  useEffect(() => {
    if (selectedIdx >= results.length) setSelectedIdx(0);
  }, [results.length, selectedIdx]);

  // Reset state when opening; restore previous focus when closing.
  useEffect(() => {
    if (open) {
      previouslyFocusedRef.current = document.activeElement;
      setQuery('');
      setSelectedIdx(0);
      // Pull recents at open-time so a file opened mid-session shows up next time.
      getRecent().then(setRecents).catch(() => setRecents([]));
      // Focus the input after the modal mounts.
      setTimeout(() => inputRef.current?.focus(), 0);
    } else if (previouslyFocusedRef.current?.focus) {
      try { previouslyFocusedRef.current.focus(); } catch { /* ignore */ }
    }
  }, [open]);

  // Cmd+K / Ctrl+K to toggle; Esc to close. Single global listener.
  // Also opens on the `scrivar:open-command-palette` custom event (fired by the
  // AppHeader search field click so the header doesn't need to know about our
  // internal state).
  useEffect(() => {
    function onKey(e) {
      const isMod = e.metaKey || e.ctrlKey;
      if (isMod && (e.key === 'k' || e.key === 'K')) {
        // Don't hijack when an input/textarea/contentEditable is focused and
        // also wants Cmd+K — but for our app, no other binding uses it.
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === 'Escape' && open) {
        e.preventDefault();
        setOpen(false);
      }
    }
    function openHandler() {
      setOpen(true);
    }
    document.addEventListener('keydown', onKey);
    document.addEventListener('scrivar:open-command-palette', openHandler);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('scrivar:open-command-palette', openHandler);
    };
  }, [open]);

  const select = useCallback((item) => {
    setOpen(false);
    if (!item) return;
    if (item.kind === 'recent' && item.file?.path && isDesktop()) {
      // Desktop: re-read the file by path and hand it to the viewer the same
      // way the menu's Open Recent does.
      window.electronAPI.readFile(item.file.path).then((buffer) => {
        const file = new File([buffer], item.file.name, { type: 'application/pdf' });
        navigate('/viewer', { state: { newDocument: true, file, filePath: item.file.path } });
      }).catch((err) => {
        console.error('Recent file open failed', err);
        navigate('/tools');
      });
      return;
    }
    if (item.kind === 'recent') {
      // Web has no path — just jump to the tools page where the user can re-pick.
      navigate('/tools');
      return;
    }
    navigate(item.path);
  }, [navigate]);

  function onInputKeyDown(e) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIdx((i) => Math.min(i + 1, Math.max(results.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      select(results[selectedIdx]);
    } else if (e.key === 'Home') {
      e.preventDefault();
      setSelectedIdx(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      setSelectedIdx(Math.max(results.length - 1, 0));
    }
  }

  // Scroll the selected option into view when keyboard-navigating.
  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector(`[data-idx="${selectedIdx}"]`);
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIdx, open]);

  if (!open) return null;

  const optionId = (idx) => `${listboxId}-opt-${idx}`;
  const selectedOptionId = results[selectedIdx] ? optionId(selectedIdx) : undefined;

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-start justify-center px-4 pt-[12vh] pb-4"
      role="presentation"
      onMouseDown={(e) => {
        // Click on the backdrop (not the dialog) closes the palette.
        if (e.target === e.currentTarget) setOpen(false);
      }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-gray-900/40 backdrop-blur-[2px]" aria-hidden="true" />

      {/* Dialog */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('common:command_palette.title', 'Command palette')}
        className="relative w-full max-w-xl bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden flex flex-col max-h-[70vh]"
      >
        {/* Input row */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
          <Search size={18} className="text-gray-400 flex-shrink-0" aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setSelectedIdx(0); }}
            onKeyDown={onInputKeyDown}
            placeholder={t('common:command_palette.placeholder', 'Jump to a tool, page, or recent file…')}
            className="flex-1 bg-transparent text-base text-gray-900 placeholder:text-gray-400 outline-none"
            autoComplete="off"
            spellCheck="false"
            role="combobox"
            aria-expanded="true"
            aria-controls={listboxId}
            aria-autocomplete="list"
            aria-activedescendant={selectedOptionId}
          />
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label={t('common:buttons.close', 'Close')}
            className="text-gray-400 hover:text-gray-600 p-1 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300"
          >
            <X size={16} />
          </button>
        </div>

        {/* Result list */}
        <div ref={listRef} id={listboxId} role="listbox" className="overflow-y-auto flex-1">
          {results.length === 0 ? (
            <div className="px-4 py-12 text-center text-sm text-gray-500">
              {t('common:command_palette.no_results', 'No matches. Try a different keyword.')}
            </div>
          ) : (
            grouped.map((section, sIdx) => (
              <div key={section.kind} className={sIdx > 0 ? 'border-t border-gray-100' : ''}>
                <div className="px-4 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                  {sectionLabel(t, section.kind)}
                </div>
                <ul>
                  {section.items.map((item) => {
                    const idx = item._index;
                    const isSelected = idx === selectedIdx;
                    const Icon = item.icon;
                    return (
                      <li
                        key={item.key}
                        id={optionId(idx)}
                        data-idx={idx}
                        role="option"
                        aria-selected={isSelected}
                        onMouseEnter={() => setSelectedIdx(idx)}
                        onClick={() => select(item)}
                        className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer ${
                          isSelected ? 'bg-indigo-50' : 'hover:bg-gray-50'
                        }`}
                      >
                        <span className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          isSelected ? 'bg-white text-indigo-600' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {Icon ? <Icon size={16} aria-hidden="true" /> : <ArrowRight size={16} aria-hidden="true" />}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-gray-900 truncate">{item.title}</div>
                          {item.subtitle && (
                            <div className="text-xs text-gray-500 truncate">{item.subtitle}</div>
                          )}
                        </div>
                        {isSelected && (
                          <CornerDownLeft size={14} className="text-indigo-500 flex-shrink-0" aria-hidden="true" />
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))
          )}
        </div>

        {/* Footer hints */}
        <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-t border-gray-100 text-[11px] text-gray-500 bg-gray-50">
          <div className="flex items-center gap-3">
            <span><Kbd>↑↓</Kbd> {t('common:command_palette.hint_navigate', 'Navigate')}</span>
            <span><Kbd>↵</Kbd> {t('common:command_palette.hint_open', 'Open')}</span>
            <span><Kbd>Esc</Kbd> {t('common:command_palette.hint_close', 'Close')}</span>
          </div>
          <span className="hidden sm:inline">
            <Kbd>{platformMod()}</Kbd> + <Kbd>K</Kbd>
          </span>
        </div>
      </div>
    </div>
  );
}

function Kbd({ children }) {
  return (
    <span className="inline-block min-w-[18px] text-center px-1.5 py-0.5 rounded border border-gray-200 bg-white text-gray-600 font-mono text-[10px] leading-none">
      {children}
    </span>
  );
}

function platformMod() {
  if (typeof navigator === 'undefined') return 'Ctrl';
  return /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘' : 'Ctrl';
}

function tResolved(t, key, fallback) {
  if (!key || !t) return fallback;
  const v = t(key);
  return v && v !== key ? v : fallback;
}

function sectionLabel(t, kind) {
  if (kind === 'recent') return t('common:command_palette.section_recent', 'Recent');
  if (kind === 'tool') return t('common:command_palette.section_tools', 'Tools');
  if (kind === 'page') return t('common:command_palette.section_pages', 'Pages');
  return kind;
}

/**
 * Score an item against a normalized query.
 *  - 100: title starts with query
 *  -  70: title contains query as a substring
 *  -  50: acronym match (q="mp" → "Merge PDF")
 *  -  20: any other haystack field contains query
 *  -   0: no match
 *
 * Exported for unit tests in __tests__/CommandPalette.score.test.js.
 */
export function scoreItem(item, q) {
  const title = (item.title || '').toLowerCase();
  if (title.startsWith(q)) return 100;
  if (title.includes(q)) return 70;
  // Acronym match: take first letter of each word in title.
  const acronym = title.split(/\s+/).map((w) => w[0] || '').join('');
  if (acronym.startsWith(q) && q.length >= 2) return 50;
  if (item.haystack && item.haystack.includes(q)) return 20;
  return 0;
}

/**
 * Group result items by kind in their *current order* (the sort order from
 * scoreItem). Each item gets an `_index` matching its global position so the
 * selection/scroll/aria targeting stays consistent across sections.
 */
function groupBySection(results) {
  const KIND_ORDER = ['recent', 'tool', 'page'];
  const buckets = new Map();
  results.forEach((item, i) => {
    const annotated = { ...item, _index: i };
    if (!buckets.has(item.kind)) buckets.set(item.kind, []);
    buckets.get(item.kind).push(annotated);
  });
  return KIND_ORDER
    .filter((k) => buckets.has(k))
    .map((k) => ({ kind: k, items: buckets.get(k) }));
}
